import { css } from 'aphrodite';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import * as Api from '@ViewModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { copyToClipboard } from '../../../models/UiUtils';
import {
	useInfiniteTeamMeetingConfigsQuery,
	useTeamMeetingConfigCreateMutation,
	useTeamMeetingConfigUpdateMutation,
	useTeamMeetingConfigDeleteMutation,
	useTeamMeetingConfigShortcutMutation,
} from '../../../queries/TeamMeetingConfig';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { baseStyleSheet, bs } from '../../styles/styles';
import { TeamMeetingConfigCard } from '../../components/settings/PersonalSettings/TeamMeetingConfigCard';
import { TeamMeetingConfigModal } from './TeamMeetingConfigModal';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

const pathComponentNameProvider = (pathComponent: string): string | null => {
	const names: Record<string, string> = {
		'schedule-meeting': 'Schedule a Meeting',
		'team-links': 'Team Meeting Links',
	};
	return names[pathComponent] ?? null;
};

export function TeamMeetingLinksPage() {
	const userSession = useUserSession();
	const isAdmin = userSession.user.groups?.includes('admin') ?? false;

	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const [editingConfig, setEditingConfig] = React.useState<Api.ITeamMeetingConfig | null>(null);

	const {
		data: configsData,
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useInfiniteTeamMeetingConfigsQuery({ pageSize: 25 });
	const createMutation = useTeamMeetingConfigCreateMutation();
	const updateMutation = useTeamMeetingConfigUpdateMutation();
	const deleteMutation = useTeamMeetingConfigDeleteMutation();
	const shortcutMutation = useTeamMeetingConfigShortcutMutation();

	// Filter to only show enabled configs (sorting handled by API)
	const enabledConfigs = React.useMemo(() => {
		const allConfigs = configsData?.pages?.flatMap(page => page.values) ?? [];
		return allConfigs.filter(config => config.enabled);
	}, [configsData]);

	const loadMore = () => {
		if (hasNextPage) {
			fetchNextPage();
		}
	};

	// Redirect non-admins
	if (!isAdmin) {
		return <Redirect to='/schedule-meeting' />;
	}

	const handleAdd = () => {
		setEditingConfig(null);
		setIsModalOpen(true);
	};

	const handleEdit = (config: Api.ITeamMeetingConfig) => {
		setEditingConfig(config);
		setIsModalOpen(true);
	};

	const handleDelete = async (config: Api.ITeamMeetingConfig) => {
		try {
			await deleteMutation.mutateAsync(config.id);
			toast.success(`"${config.name}" has been deleted.`);
		} catch (error) {
			console.error('Failed to delete team meeting link:', error);
			toast.error('Failed to delete team meeting link.');
		}
	};

	const handleCopyLink = async (config: Api.ITeamMeetingConfig) => {
		try {
			const shortcut = await shortcutMutation.mutateAsync(config.id);
			const done = copyToClipboard(shortcut.url);
			if (done) {
				toast.success('Copied booking link to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard.');
			}
		} catch (error) {
			console.error('Failed to generate booking link:', error);
			toast.error('Failed to generate booking link.');
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditingConfig(null);
	};

	const handleSave = async (
		request: Api.ITeamMeetingConfigCreateRequest | Api.ITeamMeetingConfigUpdateRequest,
		configId?: string
	) => {
		try {
			if (configId) {
				// Update existing
				await updateMutation.mutateAsync({
					id: configId,
					request: request as Api.ITeamMeetingConfigUpdateRequest,
				});
				toast.success('Team meeting link updated successfully!');
			} else {
				// Create new
				const newConfig = await createMutation.mutateAsync(request as Api.ITeamMeetingConfigCreateRequest);
				// Auto-generate shortcut
				await shortcutMutation.mutateAsync(newConfig.id);
				toast.success('Team meeting link created successfully!');
			}
			handleModalClose();
		} catch (error) {
			console.error('Failed to save team meeting link:', error);
			const baseMessage = configId ? 'Failed to update team meeting link.' : 'Failed to create team meeting link.';
			let errorDetails = '';
			if (error && typeof error === 'object') {
				if ('systemMessage' in error && typeof error.systemMessage === 'string') {
					errorDetails = error.systemMessage;
				} else if ('message' in error && typeof error.message === 'string') {
					errorDetails = error.message;
				}
			}
			toast.error(errorDetails ? `${baseMessage} Error details: ${errorDetails}` : baseMessage);
		}
	};

	return (
		<Page styles={[styleSheet.container]}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar
					currentLocationName='Team Meeting Links'
					pathComponentNameProvider={pathComponentNameProvider}
				/>
			</Portal>

			<div className={css(styleSheet.headerContainer)}>
				<h1 className={css(bs.breadcrumbTitle)}>Team Meeting Links</h1>
				<button className={css(baseStyleSheet.ctaButton)} onClick={handleAdd}>
					Add Team Meeting Link
				</button>
			</div>

			<p className={css(styleSheet.description)}>
				Team Meeting Links allow you to create a single booking link that distributes meetings across multiple team
				members. When someone books through a team link, the system automatically finds an available time slot from any
				of the configured team members.
			</p>

			{isLoading ? (
				<LoadingSpinner type='small' className={css(baseStyleSheet.absoluteCenter)} />
			) : enabledConfigs.length === 0 ? (
				<div className={css(styleSheet.emptyState)}>
					<p>No team meeting links configured yet.</p>
					<p className={css(styleSheet.emptyStateText)}>
						Click &quot;Add Team Meeting Link&quot; to create your first team booking link.
					</p>
				</div>
			) : (
				<>
					<div className={css(styleSheet.cardsContainer)}>
						<button className={css(styleSheet.addCard)} onClick={handleAdd}>
							+ Add Team Meeting Link
						</button>
						{enabledConfigs.map(config => (
							<TeamMeetingConfigCard
								key={config.id}
								config={config}
								onEdit={handleEdit}
								onDelete={handleDelete}
								onCopyLink={handleCopyLink}
							/>
						))}
					</div>
					{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={loadMore} />}
					{isFetchingNextPage && <LoadingSpinner type='small' />}
				</>
			)}

			<TeamMeetingConfigModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				config={editingConfig}
				onSave={handleSave}
			/>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}
